import cn from 'classnames';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { TranscriptChunk } from '@/shared/api/transcriptor';
import { Badge, BadgeTone, Button, Icon } from '@/shared/ui';

import type { ConsultationRecording } from '@/entities/transcriptor';

import { formatTimecode } from '../lib';

import { AiMark } from './AiMark';
import styles from './TranscriptView.module.scss';

type ChunksProps = {
  chunks: TranscriptChunk[];
  // Text of a phrase still being recognised; it may change.
  partial?: string;
  // Speaker of chunks that carry none, e.g. the clinician of a dictation.
  speaker?: string;
  className?: string;
};

export const TranscriptChunks: FC<ChunksProps> = ({
  chunks,
  partial,
  speaker,
  className,
}) => {
  const speakerOf = (chunk: TranscriptChunk) => chunk.speaker || speaker;
  // One voice (a dictation) needs no label on every phrase.
  const severalSpeakers = chunks.some(
    (chunk) => speakerOf(chunk) !== speakerOf(chunks[0]),
  );

  return (
    <ol className={cn(styles.chunks, className)}>
      {chunks.map((chunk) => {
        const label = severalSpeakers && speakerOf(chunk);
        return (
          <li key={`${chunk.start}-${chunk.end}`} className={styles.chunk}>
            <time className="p4">{formatTimecode(chunk.start)}</time>
            <p className="p2">
              {label && <span className={styles.speaker}>{label}</span>}
              {chunk.text}
            </p>
          </li>
        );
      })}
      {partial && (
        <li className={cn(styles.chunk, styles.partial)} aria-live="polite">
          <span className="p4">…</span>
          <p className="p2">{partial}</p>
        </li>
      )}
    </ol>
  );
};

type LiveCardProps = {
  recorder: ReactNode;
  notice: ReactNode;
  chunks: TranscriptChunk[];
  partial: string;
  listening: boolean;
  specialty?: string;
  className?: string;
};

// The treatment dictation while it is recorded: recorder, live status and the
// phrases recognised so far.
export const LiveTranscriptCard: FC<LiveCardProps> = ({
  recorder,
  notice,
  chunks,
  partial,
  listening,
  specialty,
  className,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div
      className={cn(styles.transcript, className)}
      data-component="workspace-live-transcript"
    >
      <div className={styles.heading}>
        <h2 className="h6">
          <FormattedMessage
            id="workspace.live.recordingTitle"
            defaultMessage="Treatment recording"
          />
        </h2>
        <span className={styles.spacer} />
        {specialty && (
          <span className={cn('p4', styles.specialty)}>{specialty}</span>
        )}
      </div>
      {recorder}
      {notice}
      <div className={styles.liveHeading}>
        <span className={cn('p4', styles.liveLabel)}>
          <FormattedMessage
            id="workspace.live.transcriptTitle"
            defaultMessage="Live transcript"
          />
        </span>
        <AiMark />
      </div>
      {chunks.length || partial ? (
        <TranscriptChunks
          chunks={chunks}
          partial={partial}
          speaker={formatMessage({
            id: 'workspace.transcript.clinician',
            defaultMessage: 'Clinician',
          })}
          className={styles.scroll}
        />
      ) : (
        <p className={cn('p3', styles.pending)}>
          <FormattedMessage
            id="workspace.live.transcriptPending"
            defaultMessage="The transcript appears here as you dictate."
          />
        </p>
      )}
      {listening && (
        <p className={cn('p4', styles.listening)}>
          <FormattedMessage
            id="workspace.recording.listening"
            defaultMessage="Listening…"
          />
        </p>
      )}
    </div>
  );
};

type Props = {
  status: string;
  statusTone: BadgeTone;
  meta: string;
  recordings: ConsultationRecording[];
  chunks: TranscriptChunk[];
  title?: ReactNode;
  speaker?: string;
  error?: string | null;
  disabled?: boolean;
  live?: { partial: string; notice: ReactNode };
  className?: string;
  onClose: () => void;
};

export const TranscriptView: FC<Props> = ({
  status,
  statusTone,
  meta,
  recordings,
  chunks,
  title,
  speaker,
  error,
  disabled,
  live,
  className,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        chunks
          .map((chunk) => `${formatTimecode(chunk.start)} ${chunk.text}`)
          .join('\n'),
      );
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };
  const copyFeedback: Record<typeof copyState, ReactNode> = {
    idle: null,
    copied: (
      <FormattedMessage
        id="workspace.transcriptCopied"
        defaultMessage="Transcript copied"
      />
    ),
    failed: (
      <FormattedMessage
        id="workspace.transcriptCopyFailed"
        defaultMessage="Could not copy. Select the text manually."
      />
    ),
  };

  return (
    <div
      className={cn(styles.transcript, className)}
      data-component="workspace-transcript"
    >
      <div className={styles.heading}>
        <h2 className="h6">
          {title ?? (
            <FormattedMessage
              id="workspace.transcriptTitle"
              defaultMessage="Encounter transcript"
            />
          )}
        </h2>
        <Badge size="small" tone={statusTone}>
          {status}
        </Badge>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.close}
          disabled={disabled}
          aria-label={formatMessage({
            id: 'global.close',
            defaultMessage: 'Close',
          })}
          onClick={onClose}
        >
          <Icon name="close" size={20} />
        </button>
      </div>
      <p className={cn('p4', styles.meta)}>{meta}</p>
      {error && (
        <p className={cn('p3', styles.alert)} role="alert">
          {error}
        </p>
      )}
      {recordings
        .filter((recording) => recording.url)
        .map((recording) => (
          <audio
            key={recording.id}
            className={styles.audio}
            controls
            preload="none"
            src={recording.url!}
          />
        ))}
      {live?.notice}
      {chunks.length || live?.partial ? (
        <TranscriptChunks
          chunks={chunks}
          partial={live?.partial}
          speaker={speaker}
          className={styles.scroll}
        />
      ) : (
        <p className={cn('p3', styles.pending)}>
          {live ? (
            <FormattedMessage
              id="workspace.live.transcriptPending"
              defaultMessage="The transcript appears here as you dictate."
            />
          ) : (
            <FormattedMessage
              id="workspace.transcriptPending"
              defaultMessage="Transcript is not available yet. Processing starts after the recording stops."
            />
          )}
        </p>
      )}
      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="medium"
          block
          disabled={disabled}
          onClick={onClose}
        >
          <FormattedMessage id="workspace.new" defaultMessage="New recording" />
        </Button>
        <Button
          variant="tertiary"
          size="medium"
          block
          disabled={!chunks.length}
          onClick={copy}
        >
          <FormattedMessage id="global.copy" defaultMessage="Copy" />
        </Button>
      </div>
      {copyState !== 'idle' && (
        <p className={cn('p4', styles.copyFeedback)} role="status">
          {copyFeedback[copyState]}
        </p>
      )}
    </div>
  );
};
